import React, { useState, useEffect, useRef } from 'react';
import { ThemeProvider } from './theme';
import { Footer, Header, ThemeToggle } from './layout';
import { getPostBySlug, getAllPosts, formatDate, BlogPost, BlogSection } from './blog';
import { BlogFigure } from './blog/BlogFigure';
import { slugifyBlogAnchor } from './blog/helpers';
import { BlogEmbed, getBlogEmbedMetadata } from './blog/embeds';
import './theme/globals.css';

/* ─── Content Renderers ─── */

const SECTION_SCROLL_CLASS = 'scroll-mt-28 sm:scroll-mt-32';

function getSectionAnchorId(section: BlogSection): string | undefined {
  if (section.id) return section.id;
  if (section.type === 'heading') return slugifyBlogAnchor(section.content);
  if (section.type === 'embed' && section.embed) {
    return getBlogEmbedMetadata(section.embed).anchorId;
  }
  return undefined;
}

function renderSection(section: BlogSection, index: number) {
  const sectionId = getSectionAnchorId(section);

  switch (section.type) {
    case 'paragraph':
      return (
        <p 
          key={index} 
          id={sectionId}
          className="text-left text-foreground-secondary leading-[1.75] mb-6 [hyphens:auto] sm:text-justify sm:[text-align-last:left] sm:[text-justify:inter-word]"
          dangerouslySetInnerHTML={{ __html: section.content.replace(/<strong>/g, '<strong class="text-foreground">') }}
        />
      );

    case 'heading':
      const headingLevel = (section.level === 3 || section.level === 4 ? section.level : 2) as 2 | 3 | 4;
      const HeadingTag = (`h${headingLevel}` as const) satisfies 'h2' | 'h3' | 'h4';
      const headingClasses = {
        2: "scroll-mt-28 sm:scroll-mt-32 text-[22px] sm:text-[26px] font-display font-medium text-foreground mt-12 mb-5 leading-[1.25] tracking-[-0.3px]",
        3: "scroll-mt-28 sm:scroll-mt-32 text-[18px] sm:text-[20px] font-display font-medium text-foreground mt-10 mb-4 leading-[1.25] tracking-[-0.2px]",
        4: 'scroll-mt-28 sm:scroll-mt-32 text-[16px] sm:text-[18px] font-medium text-foreground mt-8 mb-4',
      };
      return (
        <HeadingTag key={index} id={sectionId} className={headingClasses[headingLevel]}>
          {section.content}
        </HeadingTag>
      );

    case 'code':
      return (
        <div key={index} id={sectionId} className={`my-8 rounded-md overflow-hidden border border-border-subtle ${sectionId ? SECTION_SCROLL_CLASS : ''}`}>
          {section.language && (
            <div className="px-4 py-2 bg-foreground/[0.02] dark:bg-white/[0.02] border-b border-border-subtle">
              <span className="text-[11px] font-mono uppercase tracking-wider text-foreground-muted">
                {section.language}
              </span>
            </div>
          )}
          <pre className="p-4 overflow-x-auto bg-foreground/[0.015] dark:bg-white/[0.015]">
            <code className="text-[14px] font-mono text-foreground-secondary leading-relaxed">
              {section.content}
            </code>
          </pre>
        </div>
      );

    case 'quote':
      return (
        <blockquote
          key={index}
          id={sectionId}
          className={`my-8 pl-5 border-l-2 border-foreground/10 dark:border-white/10 italic text-foreground-secondary ${sectionId ? SECTION_SCROLL_CLASS : ''}`}
        >
          <p className="leading-relaxed">{section.content}</p>
        </blockquote>
      );

    case 'list':
      const ListTag = section.ordered ? 'ol' : 'ul';
      return (
        <div key={index} id={sectionId} className={`my-6 ${sectionId ? SECTION_SCROLL_CLASS : ''}`}>
          {section.content && (
            <p className="text-foreground-secondary leading-[1.75] mb-2"
               dangerouslySetInnerHTML={{ __html: section.content.replace(/<strong>/g, '<strong class="text-foreground">') }}
            />
          )}
          <ListTag
            className={`space-y-2 pl-6 ${
              section.ordered ? 'list-decimal' : 'list-disc'
            } list-outside text-foreground-secondary`}
          >
            {section.items?.map((item, i) => (
              <li key={i} className="leading-relaxed">
                {item}
              </li>
            ))}
          </ListTag>
        </div>
      );

    case 'callout':
      const variantStyles = {
        info: 'bg-foreground/[0.03] dark:bg-white/[0.03] border-foreground/10 dark:border-white/10',
        warning: 'bg-foreground/[0.03] dark:bg-white/[0.03] border-foreground/10 dark:border-white/10',
        tip: 'bg-foreground/[0.03] dark:bg-white/[0.03] border-foreground/10 dark:border-white/10',
        note: 'bg-foreground/[0.03] dark:bg-white/[0.03] border-foreground/10 dark:border-white/10',
      };
      return (
        <div
          key={index}
          id={sectionId}
          className={`my-8 p-5 rounded-md border ${variantStyles[section.variant || 'note']} ${sectionId ? SECTION_SCROLL_CLASS : ''}`}
        >
          <p className="leading-relaxed text-foreground-secondary text-[15px]">{section.content}</p>
        </div>
      );

    case 'image':
      const lightImageSrc = section.lightContent || section.content;
      const darkImageSrc = section.darkContent;
      const shouldContainImage = Boolean(section.imageMaxHeight);
      const figureStyle: React.CSSProperties = {};
      const imageStyle: React.CSSProperties = {};

      if (section.imageMaxWidth) {
        figureStyle.maxWidth = section.imageMaxWidth;
      }

      if (section.imageMaxHeight) {
        imageStyle.maxHeight = section.imageMaxHeight;
      }

      const imageClassName = shouldContainImage
        ? 'w-full object-contain rounded-md'
        : 'w-full rounded-md';

      return (
        <BlogFigure key={index} id={sectionId} caption={section.caption} className="my-10 mx-auto w-full" style={figureStyle}>
          <div className="mx-auto w-full">
            {darkImageSrc ? (
              <>
                <img
                  src={lightImageSrc}
                  alt={section.alt || ''}
                  className={`${imageClassName} dark:hidden`}
                  style={imageStyle}
                  loading="lazy"
                  decoding="async"
                />
                <img
                  src={darkImageSrc}
                  alt={section.alt || ''}
                  className={`${imageClassName} hidden dark:block`}
                  style={imageStyle}
                  loading="lazy"
                  decoding="async"
                />
              </>
            ) : (
              <img
                src={lightImageSrc}
                alt={section.alt || ''}
                className={imageClassName}
                style={imageStyle}
                loading="lazy"
                decoding="async"
              />
            )}
          </div>
        </BlogFigure>
      );

    case 'html':
      return (
        <div
          key={index}
          id={sectionId}
          className={`my-8 text-foreground-secondary ${sectionId ? SECTION_SCROLL_CLASS : ''}`}
          dangerouslySetInnerHTML={{ __html: section.content }}
        />
      );

    case 'embed':
      if (!section.embed) return null;
      const embedMetadata = getBlogEmbedMetadata(section.embed);
      const embedAnchorId = embedMetadata.figureNumber ? undefined : sectionId;
      const embedSpacingClass = embedMetadata.blockSpacing === 'body' ? 'my-8' : 'my-14';
      return (
        <div
          key={index}
          id={embedAnchorId}
          className={`${embedSpacingClass} w-full min-w-0 max-w-full ${embedAnchorId ? SECTION_SCROLL_CLASS : ''}`}
        >
          <BlogEmbed name={section.embed} />
        </div>
      );

    default:
      return null;
  }
}

/* ─── Table of Contents ─── */

type TocEntry = {
  id: string;
  label: string;
  level: 2 | 3;
  kind?: 'section' | 'demo';
};

function buildTableOfContentsEntries(content: BlogSection[]): TocEntry[] {
  const startsWithIntro = content.length > 0 && content[0].type !== 'heading';
  const entries: TocEntry[] = startsWithIntro
    ? [{ id: 'article-intro', label: 'Introduction', level: 2 }]
    : [];

  content.forEach(section => {
    if (section.toc === false) return;

    const explicitToc = section.toc || undefined;

    if (section.type === 'heading' && (section.level === 2 || section.level === 3 || !section.level)) {
      const id = getSectionAnchorId(section);
      if (!id) return;

      entries.push({
        id,
        label: explicitToc?.label || section.content,
        level: explicitToc?.level || section.level || 2,
        kind: explicitToc?.kind || 'section',
      });
      return;
    }

    if (explicitToc) {
      const id = getSectionAnchorId(section);
      if (!id) return;

      entries.push({
        id,
        label: explicitToc.label || section.content,
        level: explicitToc.level || 2,
        kind: explicitToc.kind || 'section',
      });
    }
  });

  return entries;
}

interface TableOfContentsProps {
  content: BlogSection[];
  articleRef: React.RefObject<HTMLDivElement>;
}

function TableOfContents({ content, articleRef }: TableOfContentsProps) {
  const [activeId, setActiveId] = useState<string>('');
  const [tocStyle, setTocStyle] = useState<React.CSSProperties>({});
  const tocEntries = React.useMemo(() => buildTableOfContentsEntries(content), [content]);

  // Calculate TOC position based on article position
  useEffect(() => {
    const updatePosition = () => {
      if (!articleRef.current) return;
      
      const articleRect = articleRef.current.getBoundingClientRect();
      const scrollY = window.scrollY;
      const articleTop = articleRect.top + scrollY;
      const articleBottom = articleTop + articleRef.current.offsetHeight;
      const stickyTop = 128; // 128px = top-32
      const viewportTop = scrollY + stickyTop;
      
      // Calculate left position - fixed position relative to viewport
      const leftPos = articleRect.left - 200 - 32; // 200px width + 32px gap
      
      if (leftPos < 8) {
        // Not enough space, hide TOC
        setTocStyle({ display: 'none' });
        return;
      }
      
      if (viewportTop < articleTop) {
        // Before article starts sticking: absolute position at top of article
        setTocStyle({
          position: 'absolute',
          top: 0,
          left: -232, // -200px width - 32px gap
          width: 200,
        });
      } else if (viewportTop >= articleTop && viewportTop < articleBottom - 300) {
        // During article: fixed position
        setTocStyle({
          position: 'fixed',
          top: stickyTop,
          left: leftPos,
          width: 200,
        });
      } else {
        // Near end of article: absolute position at bottom
        setTocStyle({
          position: 'absolute',
          bottom: 100,
          left: -232,
          width: 200,
        });
      }
    };

    updatePosition();
    window.addEventListener('scroll', updatePosition, { passive: true });
    window.addEventListener('resize', updatePosition, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', updatePosition);
      window.removeEventListener('resize', updatePosition);
    };
  }, [articleRef]);

  // Track active heading based on scroll position
  // Use a consistent offset (128px) for both scrolling and active detection
  const SCROLL_OFFSET = 128;
  
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + SCROLL_OFFSET + 20; // Add small buffer
      
      // Find the last heading that's above the scroll position
      let currentActive = '';
      for (const entry of tocEntries) {
        const element = document.getElementById(entry.id);
        if (element) {
          const elementTop = element.getBoundingClientRect().top + window.scrollY;
          if (elementTop <= scrollPosition) {
            currentActive = entry.id;
          } else {
            break;
          }
        }
      }
      
      if (currentActive && currentActive !== activeId) {
        setActiveId(currentActive);
      }
    };

    handleScroll(); // Initial check
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, [tocEntries, activeId]);

  if (tocEntries.length < 2) return null;

  // Scroll handler that accounts for fixed header
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const elementTop = element.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({
        top: elementTop - SCROLL_OFFSET,
        behavior: 'smooth'
      });
      setActiveId(id);
    }
  };

  return (
    <nav className="hidden min-[1160px]:block" style={tocStyle}>
      <ul className="space-y-2">
        {tocEntries.map((entry, index) => {
          const isActive = activeId === entry.id;
          const isDemo = entry.kind === 'demo';
          return (
            <li
              key={index}
              className={`${entry.level === 3 ? 'pl-3' : ''} ${isDemo ? 'py-1' : ''}`}
            >
              <a
                href={`#${entry.id}`}
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection(entry.id);
                }}
                className={isDemo
                  ? `group flex items-center justify-between gap-2 rounded-full border px-3 py-2 text-[13px] leading-none transition-all ${
                      isActive
                        ? 'border-brand-mint/60 bg-brand-mint/10 text-foreground'
                        : 'border-foreground/10 bg-foreground/[0.025] text-foreground-secondary hover:border-brand-mint/40 hover:bg-brand-mint/[0.06] hover:text-foreground'
                    }`
                  : `block text-[14px] leading-[1.4] transition-colors ${
                      isActive
                        ? 'text-foreground'
                        : 'text-foreground-muted hover:text-foreground-secondary'
                    }`}
              >
                {isDemo ? (
                  <>
                    <span className="flex items-center gap-2">
                      <span className="h-1.5 w-1.5 rounded-full bg-brand-mint" aria-hidden="true" />
                      {entry.label}
                    </span>
                    <span className="font-mono text-[12px] text-foreground-muted transition-transform group-hover:translate-y-0.5" aria-hidden="true">
                      ↓
                    </span>
                  </>
                ) : entry.label}
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

/* ─── Post Navigation ─── */

function PostNavigation({ currentSlug }: { currentSlug: string }) {
  const allPosts = getAllPosts();
  const currentIndex = allPosts.findIndex(p => p.slug === currentSlug);
  const prevPost = currentIndex < allPosts.length - 1 ? allPosts[currentIndex + 1] : null;
  const nextPost = currentIndex > 0 ? allPosts[currentIndex - 1] : null;

  if (!prevPost && !nextPost) return null;

  return (
    <div className="mt-16 pt-8 border-t border-border-subtle flex items-center justify-between">
      {prevPost ? (
        <a
          href={`/blog/${prevPost.slug}`}
          className="text-small text-foreground-muted hover:text-foreground transition-colors"
        >
          prev
        </a>
      ) : (
        <span />
      )}
      
      {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
      <a
        href="#"
        onClick={(e) => {
          e.preventDefault();
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }}
        className="text-small text-foreground-muted hover:text-foreground transition-colors"
      >
        back to top
      </a>
      
      {nextPost ? (
        <a
          href={`/blog/${nextPost.slug}`}
          className="text-small text-foreground-muted hover:text-foreground transition-colors"
        >
          next
        </a>
      ) : (
        <span />
      )}
    </div>
  );
}

/* ─── Post Header ─── */

function PostHeader({ post }: { post: BlogPost }) {
  const [isVisible, setIsVisible] = useState(false);
  const hasAuthors = post.authors && post.authors.length > 0;
  const titleLines = post.titleLines?.length ? post.titleLines : [post.title];

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <header className="text-center mb-10">
      {/* Title */}
      <h1
        className={`font-display text-[27px] min-[480px]:text-[28px] sm:text-[32px] md:text-[36px] leading-[1.25] tracking-[-0.5px] font-medium text-foreground max-w-[600px] mx-auto mb-6 opacity-0 ${
          isVisible ? 'animate-fade-up' : ''
        }`}
        style={{ animationDelay: '0.1s' }}
        aria-label={post.title}
      >
        {titleLines.map((line, index) => (
          <React.Fragment key={`${index}-${line}`}>
            {index > 0 && <br />}
            {line}
          </React.Fragment>
        ))}
      </h1>

      {/* Meta - Author (optional) and Date */}
      <div
        className={`flex flex-wrap items-center justify-center gap-x-1 text-[15px] opacity-0 ${
          isVisible ? 'animate-fade-up' : ''
        }`}
        style={{ animationDelay: '0.2s' }}
      >
        {hasAuthors && (
          <>
            <span className="text-foreground">
              {post.authors?.join(', ')}
            </span>
            {post.collaboration && (
              <span className="text-foreground-muted"> {post.collaboration}</span>
            )}
          </>
        )}
        
        {hasAuthors && (
          <span className="text-foreground-muted mx-2">·</span>
        )}
        
        <time className="text-foreground-muted">{formatDate(post.date)}</time>
      </div>
    </header>
  );
}

/* ─── Post Content ─── */

function PostContent({ post }: { post: BlogPost }) {
  const [isVisible, setIsVisible] = useState(false);
  const articleRef = useRef<HTMLDivElement>(null);
  
  const hasToc = buildTableOfContentsEntries(post.content).length >= 2;
  const startsWithIntro = post.content.length > 0 && post.content[0].type !== 'heading';

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 300);
    return () => clearTimeout(timer);
  }, []);

  // Render content with intro section wrapper if needed
  const renderContent = () => {
    if (!startsWithIntro) {
      return post.content.map((section, index) => renderSection(section, index));
    }
    
    // Find the index of the first heading
    const firstHeadingIndex = post.content.findIndex(s => s.type === 'heading');
    const introContent = firstHeadingIndex === -1 
      ? post.content 
      : post.content.slice(0, firstHeadingIndex);
    const restContent = firstHeadingIndex === -1 
      ? [] 
      : post.content.slice(firstHeadingIndex);
    
    return (
      <>
        <div id="article-intro" className="scroll-mt-28 sm:scroll-mt-32">
          {introContent.map((section, index) => renderSection(section, index))}
        </div>
        {restContent.map((section, index) => renderSection(section, introContent.length + index))}
      </>
    );
  };

  return (
    <div className={`opacity-0 ${isVisible ? 'animate-fade-in' : ''}`}>
      {/* Cover image */}
      {post.coverImage && (
        <figure className="mb-10 mx-0">
          <img
            src={post.coverImage}
            alt={post.title}
            className="w-full rounded-md"
          />
        </figure>
      )}

      {/* Article body with relative positioning for TOC */}
      <div ref={articleRef} className="relative">
        {/* Table of Contents */}
        {hasToc && <TableOfContents content={post.content} articleRef={articleRef} />}

        {/* Main content */}
        <div className="prose-sqwish">
          {renderContent()}
        </div>

        {/* Navigation */}
        <PostNavigation currentSlug={post.slug} />
      </div>
    </div>
  );
}

/* ─── 404 State ─── */

function PostNotFound() {
  return (
    <div className="min-h-screen bg-background text-foreground flex items-center justify-center">
      <div className="text-center px-6">
        <h1 className="font-display text-[28px] text-foreground mb-4">Post Not Found</h1>
        <p className="text-foreground-secondary mb-8">
          The blog post you're looking for doesn't exist or has been removed.
        </p>
        <a
          href="/blog"
          className="text-small text-foreground-muted hover:text-foreground transition-colors"
        >
          ← Back to Blog
        </a>
      </div>
    </div>
  );
}

/* ─── Main Blog Post Content ─── */

function BlogPostContent() {
  const slug = window.location.pathname
    .replace(/^\/blog\/?/, '')
    .replace(/\/$/, '');
  const post = getPostBySlug(slug);

  useEffect(() => {
    const scrollToHash = () => {
      const targetId = window.location.hash.slice(1);
      if (!targetId) return;
      document.getElementById(targetId)?.scrollIntoView({ block: 'start' });
    };

    const frame = window.requestAnimationFrame(scrollToHash);
    window.addEventListener('hashchange', scrollToHash);

    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('hashchange', scrollToHash);
    };
  }, [slug]);

  if (!post) {
    return (
      <>
        <Header />
        <ThemeToggle />
        <PostNotFound />
      </>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />
      <ThemeToggle />

      <main className="pt-28 sm:pt-36 pb-20 sm:pb-28">
        <div className="relative max-w-[760px] min-[1160px]:left-[60px] xl:left-0 mx-auto px-6 sm:px-8">
          <PostHeader post={post} />
          <PostContent post={post} />
        </div>
      </main>

      <Footer />
    </div>
  );
}

/* ─── Blog Post Page Export ─── */

export function BlogPostPage() {
  return (
    <ThemeProvider defaultTheme="light">
      <BlogPostContent />
    </ThemeProvider>
  );
}

export default BlogPostPage;
